import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Factory, AlertTriangle, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { GradButton, fmtMoney } from "@/components/erp";
import api, { errMsg } from "@/lib/api";
import { katalog, agHatasi, bekleyenKaydet } from "@/lib/katalog";
import { FiyatUyari } from "@/components/bayi/BayiUrunler";

const yerelHesap = (uid, kalemler) => {
  const k = katalog(uid);
  if (!k) return null;
  const m = new Map(k.urunler.map((u) => [u.id, u]));
  let ara = 0, kdv = 0, eksik = false;
  const l = kalemler.map((s) => {
    const u = m.get(s.id);
    if (!u || u.fiyat == null) { eksik = true; return { id: s.id }; }
    const tutar = u.fiyat * Number(s.miktar);
    ara += tutar; kdv += tutar * (u.kdv_orani ?? 20) / 100;
    return { id: s.id, tutar, kampanya: u.kampanya };
  });
  return { kalemler: l, ara_toplam: ara, kdv_toplam: kdv, genel_toplam: ara + kdv, para_birimi: k.para_birimi, fiyat_eksik: eksik, cevrimdisi: true };
};

const tarih = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "");

const Satir = ({ k, onMiktar, onSil, cevrimdisi }) => (
  <div className="py-3 border-b border-white/5 space-y-1.5" data-testid={`sepet-satir-${k.kod}`}>
    <div className="flex justify-between gap-2">
      <div className="min-w-0"><div className="font-mono text-[11px] text-muted-foreground">{k.kod}</div><div className="text-sm truncate">{k.ad}</div></div>
      <button data-testid={`sepet-sil-${k.kod}`} onClick={onSil} className="p-1.5 h-fit rounded-lg text-muted-foreground hover:text-red-300 hover:bg-white/5"><Trash2 className="w-4 h-4" /></button>
    </div>
    <div className="flex items-center justify-between gap-2">
      <Input type="number" min="1" value={k.miktar} onChange={(e) => onMiktar(e.target.value)} className="h-8 w-24 bg-white/5" data-testid={`sepet-miktar-${k.kod}`} />
      <span className="font-mono text-sm text-right">{k.tutar != null ? fmtMoney(k.tutar, k.pb) : ""}
        {k.kampanya && <span className="block text-[10px] text-rose-300" data-testid={`sepet-kampanya-${k.kod}`}>%{k.kampanya.yuzde} kampanya uygulandı</span>}</span>
    </div>
    {cevrimdisi && <FiyatUyari testid={`sepet-fiyat-uyari-${k.kod}`} />}
    {k.uretim > 0 && (
      <div className="text-xs text-amber-200 flex items-start gap-1.5" data-testid={`sepet-uretim-${k.kod}`}>
        <Factory className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>{k.stoktan > 0 ? `${k.stoktan} adet stoktan, ` : ""}{k.uretim} adet üretimden gelir · tahmini termin {tarih(k.termin?.tarih)}</span>
      </div>
    )}
  </div>
);

export const BayiSepet = ({ sepet, setSepet, onGonderildi, uid, cevrimici, onKuyruk }) => {
  const [h, setH] = useState(null);
  const [not, setNot] = useState("");
  const [busy, setBusy] = useState(false);
  const gecerli = sepet.filter((s) => Number(s.miktar) > 0);
  useEffect(() => {
    if (!gecerli.length) { setH(null); return undefined; }
    if (!cevrimici) { setH(yerelHesap(uid, gecerli)); return undefined; }
    const t = setTimeout(() => {
      api.post("/bayi/sepet-hesapla", { kalemler: gecerli.map((s) => ({ urun_id: s.id, miktar: Number(s.miktar) })) }).then((r) => setH(r.data))
        .catch((e) => { if (agHatasi(e)) setH(yerelHesap(uid, gecerli)); else toast.error(errMsg(e)); });
    }, 400);
    return () => clearTimeout(t);
  }, [JSON.stringify(gecerli), cevrimici]); // eslint-disable-line react-hooks/exhaustive-deps
  const bilgi = Object.fromEntries((h?.kalemler || []).map((k) => [k.id, k]));
  const kuyruga = () => {
    bekleyenKaydet(uid, { istemci_no: `${uid}-${Date.now()}`, kalemler: gecerli.map((s) => ({ id: s.id, kod: s.kod, ad: s.ad, miktar: Number(s.miktar) })), notlar: not, zaman: new Date().toISOString() });
    toast.success("İnternet yok: siparişiniz kaydedildi, bağlantı gelince otomatik gönderilecek");
    setNot(""); onKuyruk();
  };
  const gonder = async () => {
    if (!cevrimici) { kuyruga(); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/bayi/siparis", { kalemler: gecerli.map((s) => ({ urun_id: s.id, miktar: Number(s.miktar) })), notlar: not });
      toast.success(["onay_bekliyor", "gelen"].includes(data.durum) ? `${data.no} alındı, firma onayına gönderildi` : `${data.no} alındı ve işleme konuldu`);
      setNot(""); onGonderildi();
    } catch (e) { if (agHatasi(e)) kuyruga(); else toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!sepet.length) return <p className="text-sm text-muted-foreground py-8 text-center" data-testid="sepet-bos">Sepetiniz boş. Ürünlerden ekleyin.</p>;
  return (
    <div data-testid="bayi-sepet">
      {sepet.map((s) => (
        <Satir key={s.id} k={{ ...s, ...(bilgi[s.id] || {}), miktar: s.miktar, pb: h?.para_birimi }} cevrimdisi={!!h?.cevrimdisi}
          onMiktar={(v) => setSepet(sepet.map((x) => (x.id === s.id ? { ...x, miktar: v } : x)))} onSil={() => setSepet(sepet.filter((x) => x.id !== s.id))} />
      ))}
      {h && (
        <div className="pt-3 space-y-1 text-sm" data-testid="sepet-toplam">
          <div className="flex justify-between text-muted-foreground"><span>Ara toplam</span><span className="font-mono">{fmtMoney(h.ara_toplam, h.para_birimi)}</span></div>
          <div className="flex justify-between text-muted-foreground"><span>KDV</span><span className="font-mono">{fmtMoney(h.kdv_toplam, h.para_birimi)}</span></div>
          <div className="flex justify-between font-semibold text-base"><span>Genel toplam</span><span className="font-mono" data-testid="sepet-genel-toplam">{fmtMoney(h.genel_toplam, h.para_birimi)}</span></div>
          {h.cevrimdisi && <div className="text-xs text-amber-300 pt-1" data-testid="sepet-cevrimdisi">İnternet yok: tutarlar telefona kaydedilen son fiyatlarla hesaplandı. Gönder'e basarsanız sipariş bağlantı gelince güncel fiyat ve kurallarla otomatik gönderilir.</div>}
          {h.termin && <div className="text-xs text-amber-200 pt-1" data-testid="sepet-termin">Tahmini teslime hazır: {tarih(h.termin)}</div>}
          {h.onaya_duser && <div className="text-xs text-amber-300 flex gap-1.5 pt-1" data-testid="sepet-onay-uyari"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />Bu sipariş firma onayından sonra işleme alınır ({h.kural.toLowerCase()}).</div>}
          {h.fiyat_eksik && <div className="text-xs text-red-300 pt-1" data-testid="sepet-fiyat-eksik">Bazı fiyatlar hesaplanamıyor (döviz kuru tanımlı değil). Lütfen firmayla iletişime geçin.</div>}
        </div>
      )}
      <Textarea data-testid="sepet-not" placeholder="Sipariş notu (isteğe bağlı)" value={not} onChange={(e) => setNot(e.target.value)} className="mt-4 bg-white/5" maxLength={1000} />
      <GradButton data-testid="sepet-gonder" onClick={gonder} disabled={busy || !h || h.fiyat_eksik || !gecerli.length} className="w-full justify-center mt-3">
        <Send className="w-4 h-4" />{busy ? "Gönderiliyor…" : cevrimici ? "Siparişi gönder" : "Bağlantı gelince gönder"}</GradButton>
    </div>
  );
};
